import { noop } from "lodash";
import {
  type Dispatch,
  type ReactNode,
  createContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

export interface State {
  open: boolean;
  id: string | null; // used for a11y and interpolates into id and ARIA attributes
  content: ReactNode;
  triggerEl?: HTMLButtonElement;
  timeoutId?: number;
}

export const initialState: State = {
  open: false,
  id: null,
  content: null,
};

export enum ActionType {
  Open = "open",
  Close = "close",
  SetTimeout = "setTimeout",
  ClearTimeout = "clearTimeout",
}

export type ContentPayload = Pick<State, "id" | "content" | "triggerEl">;

export type Action =
  | {
      type: ActionType.Open;
      payload?: ContentPayload;
    }
  | {
      type: ActionType.Close;
    }
  | {
      type: ActionType.SetTimeout;
      payload: Pick<State, "timeoutId">;
    }
  | {
      type: ActionType.ClearTimeout;
    };

export const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case ActionType.Open:
      return {
        ...state,
        ...action.payload,
        open: true,
      };

    case ActionType.Close:
      return {
        ...initialState,
        timeoutId: state.timeoutId,
      };

    case ActionType.SetTimeout:
      return {
        ...state,
        timeoutId: action.payload.timeoutId,
      };

    case ActionType.ClearTimeout:
      clearTimeout(state.timeoutId);
      return {
        ...state,
        timeoutId: undefined,
      };

    default:
      throw Error("Unknown action type");
  }
};

type HoverCardContextType = { state: State; dispatch: Dispatch<Action> };

export const HoverCardContext = createContext<HoverCardContextType>({
  state: initialState,
  dispatch: noop,
});

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!state.open) {
      return;
    }

    const handleKeyDown = ({ key }) => {
      if (key === "Escape") {
        dispatch({ type: ActionType.Close });
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [state, dispatch]);

  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);
  return <HoverCardContext.Provider value={contextValue}>{children}</HoverCardContext.Provider>;
};

export default HoverCardContext;
